import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { fetchSessions, deleteSession } from '../../services/api';
import { CloseIcon } from '../icons';
import type { UserSession } from '../../types';

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SessionProps {
  session: UserSession;
  isCurrent: boolean;
  onDelete: (sessionId: string) => Promise<void>;
}

function Session({ session, isCurrent, onDelete }: SessionProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(session.sessionId);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div class={`session-row${isCurrent ? ' session-current' : ''}`}>
      <div class="session-info">
        <span class="session-client">
          {session.clientName || 'Unknown client'}
          {isCurrent && <span class="session-current-badge">current</span>}
        </span>
        <span class="session-meta">
          {session.lastAccessedAt
            ? `Active ${formatDate(session.lastAccessedAt)}`
            : `Created ${formatDate(session.createdAt)}`}
        </span>
      </div>
      {!isCurrent && (
        <button
          class="session-delete-btn"
          onClick={handleDelete}
          disabled={isDeleting}
          title="Revoke session"
        >
          <CloseIcon size={14} />
        </button>
      )}
    </div>
  );
}

export function SessionList() {
  const [currentSession, setCurrentSession] = useState<UserSession | null>(null);
  const [otherSessions, setOtherSessions] = useState<UserSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchSessions();
      setCurrentSession(result.currentSession);
      // Sort by lastAccessedAt (most recent first), then createdAt
      const sorted = [...result.otherSessions].sort((a, b) => {
        const aTime = a.lastAccessedAt || a.createdAt;
        const bTime = b.lastAccessedAt || b.createdAt;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
      setOtherSessions(sorted);
    } catch (err) {
      setError('Failed to load sessions');
      console.error('[Vertex] Error loading sessions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      await deleteSession(sessionId);
      setOtherSessions(prev => prev.filter(s => s.sessionId !== sessionId));
    } catch (err) {
      console.error('[Vertex] Error deleting session:', err);
      setError('Failed to delete session');
    }
  };

  const totalCount = (currentSession ? 1 : 0) + otherSessions.length;

  if (isLoading) {
    return (
      <div class="sessions-section">
        <div class="sessions-header">Sessions</div>
        <div class="sessions-loading">Loading...</div>
      </div>
    );
  }

  if (error && totalCount === 0) {
    return (
      <div class="sessions-section">
        <div class="sessions-header">Sessions</div>
        <div class="sessions-error">{error}</div>
      </div>
    );
  }

  return (
    <div class="sessions-section">
      <div class="sessions-header">Sessions</div>
      {error && <div class="sessions-error">{error}</div>}
      {totalCount === 0 ? (
        <div class="sessions-empty">No active sessions</div>
      ) : (
        <div class="sessions-list">
          {currentSession && (
            <Session
              key={currentSession.sessionId}
              session={currentSession}
              isCurrent={true}
              onDelete={handleDelete}
            />
          )}
          {otherSessions.map(session => (
            <Session
              key={session.sessionId}
              session={session}
              isCurrent={false}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
